<template>
  <div class="tag-config">
    <el-card>
      <template #header>
        <div class="card-header">
          <span>促销标签管理</span>
          <el-button type="primary" size="small" @click="handleAdd">添加标签</el-button>
        </div>
      </template>

      <el-tabs v-model="activeTab" @tab-change="handleTabChange">
        <el-tab-pane label="秒杀" name="seckill" />
        <el-tab-pane label="新人专享" name="newbie" />
        <el-tab-pane label="折扣" name="discount" />
      </el-tabs>

      <el-table :data="list" border stripe>
        <el-table-column prop="id" label="ID" width="60" />
        <el-table-column prop="product_name" label="商品名称" min-width="120" />
        <el-table-column label="原价" width="90">
          <template #default="{ row }">{{ row.original_price }}</template>
        </el-table-column>
        <el-table-column label="促销价" width="100">
          <template #default="{ row }">
            <el-tag :type="activeTab === 'seckill' ? 'danger' : activeTab === 'newbie' ? 'success' : 'warning'">
              {{ row.tag_price }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="tag_stock" label="库存" width="80" />
        <el-table-column prop="sold" label="已售" width="70" />
        <el-table-column prop="limit_per_user" label="限购" width="70" />
        <el-table-column label="启用" width="80">
          <template #default="{ row }">
            <el-switch v-model="row.is_enabled" :active-value="1" :inactive-value="0" @change="handleToggle(row)" />
          </template>
        </el-table-column>
        <el-table-column label="排序" width="90">
          <template #default="{ row }">
            <el-input-number v-model="row.sort_order" :min="0" :max="99" size="small" controls-position="right" @change="handleSortChange(row)" />
          </template>
        </el-table-column>
        <el-table-column label="操作" width="150" fixed="right">
          <template #default="{ row }">
            <el-button size="small" @click="handleEdit(row)">编辑</el-button>
            <el-button size="small" type="danger" @click="handleDelete(row)">删除</el-button>
          </template>
        </el-table-column>
      </el-table>
    </el-card>

    <el-dialog v-model="dialogVisible" :title="editingId ? '编辑标签' : '添加标签'" width="500px">
      <el-form :model="form" label-width="80px">
        <el-form-item label="商品ID">
          <el-input-number v-model="form.product_id" :min="1" :disabled="!!editingId" />
        </el-form-item>
        <el-form-item label="标签类型">
          <el-select v-model="form.tag_type" disabled>
            <el-option label="秒杀" value="seckill" />
            <el-option label="新人专享" value="newbie" />
            <el-option label="折扣" value="discount" />
          </el-select>
        </el-form-item>
        <el-form-item label="促销价">
          <el-input-number v-model="form.tag_price" :min="0" :precision="2" />
        </el-form-item>
        <el-form-item label="库存">
          <el-input-number v-model="form.tag_stock" :min="0" />
        </el-form-item>
        <el-form-item label="限购数量">
          <el-input-number v-model="form.limit_per_user" :min="1" />
        </el-form-item>
        <el-form-item label="开始时间">
          <el-date-picker v-model="form.start_time" type="datetime" placeholder="可选" />
        </el-form-item>
        <el-form-item label="结束时间">
          <el-date-picker v-model="form.end_time" type="datetime" placeholder="可选" />
        </el-form-item>
        <el-form-item label="启用">
          <el-switch v-model="form.is_enabled" :active-value="1" :inactive-value="0" />
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="dialogVisible = false">取消</el-button>
        <el-button type="primary" @click="handleSubmit">确定</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import request from '@/utils/request'
import { ElMessage, ElMessageBox } from 'element-plus'

const activeTab = ref('seckill')
const list = ref([])
const dialogVisible = ref(false)
const editingId = ref(null)
const form = ref({
  product_id: null,
  tag_type: 'seckill',
  tag_price: 0,
  tag_stock: 0,
  limit_per_user: 99,
  start_time: null,
  end_time: null,
  is_enabled: 1,
  sort_order: 0
})

const loadList = async () => {
  try {
    const res = await request.get('/api/admin/product-tag-config', { params: { tag_type: activeTab.value } })
    list.value = res.data || []
  } catch (e) {
    ElMessage.error('加载列表失败')
  }
}

const handleTabChange = () => {
  loadList()
}

const handleAdd = () => {
  editingId.value = null
  form.value = {
    product_id: null,
    tag_type: activeTab.value,
    tag_price: 0,
    tag_stock: 0,
    limit_per_user: 99,
    start_time: null,
    end_time: null,
    is_enabled: 1,
    sort_order: 0
  }
  dialogVisible.value = true
}

const handleEdit = (row) => {
  editingId.value = row.id
  form.value = {
    product_id: row.product_id,
    tag_type: row.tag_type,
    tag_price: parseFloat(row.tag_price),
    tag_stock: row.tag_stock,
    limit_per_user: row.limit_per_user,
    start_time: row.start_time,
    end_time: row.end_time,
    is_enabled: row.is_enabled,
    sort_order: row.sort_order
  }
  dialogVisible.value = true
}

const handleSubmit = async () => {
  try {
    if (editingId.value) {
      await request.put('/api/admin/product-tag-config/' + editingId.value, form.value)
    } else {
      await request.post('/api/admin/product-tag-config', form.value)
    }
    ElMessage.success(editingId.value ? '更新成功' : '添加成功')
    dialogVisible.value = false
    loadList()
  } catch (e) {
    ElMessage.error(e.response?.data?.message || '操作失败')
  }
}

const handleToggle = async (row) => {
  try {
    await request.put('/api/admin/product-tag-config/' + row.id, {
      tag_price: parseFloat(row.tag_price),
      tag_stock: row.tag_stock,
      limit_per_user: row.limit_per_user,
      start_time: row.start_time,
      end_time: row.end_time,
      is_enabled: row.is_enabled,
      sort_order: row.sort_order
    })
    ElMessage.success('更新成功')
  } catch (e) {
    ElMessage.error('更新失败')
    row.is_enabled = row.is_enabled === 1 ? 0 : 1
  }
}

const handleSortChange = async (row) => {
  try {
    await request.put('/api/admin/product-tag-config/' + row.id, {
      tag_price: parseFloat(row.tag_price),
      tag_stock: row.tag_stock,
      limit_per_user: row.limit_per_user,
      start_time: row.start_time,
      end_time: row.end_time,
      is_enabled: row.is_enabled,
      sort_order: row.sort_order
    })
  } catch (e) {
    ElMessage.error('排序更新失败')
  }
}

const handleDelete = async (row) => {
  try {
    await ElMessageBox.confirm('确定删除此促销标签？', '提示', { type: 'warning' })
    await request.delete('/api/admin/product-tag-config/' + row.id)
    ElMessage.success('删除成功')
    loadList()
  } catch (e) {
    if (e !== 'cancel') {
      ElMessage.error('删除失败')
    }
  }
}

onMounted(() => {
  loadList()
})
</script>

<style scoped>
.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}
</style>
